import _ from "lodash";
import { AttachmentList } from "Attachment";
import React, { useState } from "react";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import I18nHOC from "UIComponents/I18nHOC";
import ProfileCascade from "UIComponents/ProfileCascade";
import TextMoreLess from "UIComponents/TextMoreLess";
import UIModal from "UIComponents/UIModal";
import classes from "./StudentResponse.scss";
import { TableColored, GoalsOutlined } from "@toddle-edu/ds-icons";
import { Button } from "@toddle-edu/ds-web";
import { ASSESSMENT_TOOLS } from "@toddle-edu/common-utils";
import { getCriteriaSetOfPypType } from "modules/Services";
import { AssessmentToolPreviewModalV2 } from "UnitPlans/components/LibraryCommonComponents";
import {
  getAttachmentsToDisplayWithPostConfig,
  checkAchievementCriteriaExists,
  checkAssessmentHasPypEvaluation,
  getAssessmentToolText,
  getShowAssessmentTool,
  reorderTools,
  getStaffElementRemarkMessage,
} from "ClassRoom/modules/utils";
import classNames from "classnames";
import PypEvaluation from "AssessmentEvaluation/components/PypEvaluation";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import {
  getSelectedAcademicYearId,
  getDefaultStudentIdentifierNamePreference,
} from "modules/CommonSelectors";
import { getStudentIdentifierName } from "Utils/GetFormattedStudentName";

const styles = {
  profileCascadeStyle: {
    borderWidth: 0,
    width: 20,
    height: 20,
  },
  rubricConStyle: {
    width: "100%",
  },
  avatarStyle: {
    size: "x-small",
    shade: "light",
    randomizeColor: true,
  },
};

export const StudentResponseProfile = props => {
  const { student, t, defaultStudentIdentifierNamePreference } = props;
  const { id, firstName, lastName, profileImage } = student;
  return (
    <div className={classes.profileContainer}>
      <ProfileCascade
        items={[
          {
            id: id,
            value: `${firstName} ${lastName ?? ""}`,
            imageUrl: profileImage,
          },
        ]}
        showAvatar={true}
        avatarStyles={styles.avatarStyle}
      />

      <div className={classes.name}>
        {t("common:response_with_name", {
          name: getStudentIdentifierName({
            student,
            defaultStudentIdentifierNamePreference,
          }),
        })}
      </div>
    </div>
  );
};

const renderAssessmentToolCard = params => {
  const {
    t,
    node,
    setShowAssessmentTool,
    evaluationContentClass,
    setAssessmentToolInfo,
    assessmentFields,
  } = params;

  const assessmentTool = _.get(node, "assessmentToolItem", {});
  const assessmentToolId = _.get(node, "assessmentToolItem.id", "");
  const assessmentToolType = _.get(node, "assessmentToolType", "");

  if (assessmentToolType === "SCORE") return;

  const isTeacherEvaluationEnable = _.get(
    node,
    "isTeacherEvaluationEnable",
    false
  );
  const isStudentEvaluationEnable = _.get(
    node,
    "isStudentEvaluationEnable",
    false
  );

  const onClickAssessmentToolCard = () => {
    setShowAssessmentTool(true);
    if (setAssessmentToolInfo) {
      setAssessmentToolInfo({
        assessmentToolIdLocal: assessmentToolId,
        assessmentToolTypeLocal: assessmentToolType,
        isTeacherEvaluationEnabledLocal: isTeacherEvaluationEnable,
        isStudentEvaluationEnabledLocal: isStudentEvaluationEnable,
      });
    }
  };

  const { assessmentToolText } = getAssessmentToolText({
    t,
    assessmentToolType,
    assessmentTool,
  });

  const showAssessmentToolCard =
    getShowAssessmentTool({
      assessmentTool,
      assessmentToolType,
      allFields: assessmentFields,
    }) &&
    (isTeacherEvaluationEnable || isStudentEvaluationEnable);

  return (
    showAssessmentToolCard && (
      <div
        className={evaluationContentClass}
        onClick={onClickAssessmentToolCard}
        key={_.get(node, "id", "")}
      >
        <div className={classes.evaluationIcon}>
          <TableColored size={"xx-small"} />
        </div>
        <div className={classes.evaluationTitle}>{assessmentToolText}</div>
      </div>
    )
  );
};

const StudentResponseEvaluation = props => {
  const {
    evaluationTextStyle,
    isPreviewCard,
    setShowAssessmentTool,
    t,
    isAttachmentGroupEmpty,
    setShowRateLearningGoalsModal,
    showRateLearningGoals,
    assessmentTools,
    setAssessmentToolInfo,
    isTeacherEvaluationEnabled,
    isStudentEvaluationEnabled,
    assessmentFields,
  } = props;

  const evaluationContentContainerClass = classNames({
    [classes.evaluationContentContainer]: !isPreviewCard,
    [classes.evaluationContentContainerPreview]: isPreviewCard,
  });

  const evaluationContentClass = classNames({
    [classes.evaluationContent]: true,
    [classes.evaluationContentPreview]: isPreviewCard,
  });

  const showAssessmentToolCard =
    !_.isEmpty(assessmentTools) &&
    _.some(assessmentTools, ({ node }) => {
      const isTeacherEvaluationEnable = _.get(
        node,
        "isTeacherEvaluationEnable",
        false
      );
      const isStudentEvaluationEnable = _.get(
        node,
        "isStudentEvaluationEnable",
        false
      );

      return isTeacherEvaluationEnable || isStudentEvaluationEnable;
    });

  return (
    (showAssessmentToolCard || showRateLearningGoals) && (
      <div
        className={classes.evaluationContainer}
        style={{
          marginLeft: isPreviewCard && !isAttachmentGroupEmpty ? "16px" : "",
        }}
      >
        {!isPreviewCard && (
          <div className={classes.evaluationText} style={evaluationTextStyle}>
            {t("common:evaluation")}
          </div>
        )}
        <div className={evaluationContentContainerClass}>
          {showAssessmentToolCard &&
            _.map(
              reorderTools({ assessmentTools, hasEdges: false }),
              ({ node }) => {
                return renderAssessmentToolCard({
                  t,
                  evaluationContentClass,
                  node,
                  setShowAssessmentTool,
                  setAssessmentToolInfo,
                  isTeacherEvaluationEnabled,
                  isStudentEvaluationEnabled,
                  assessmentFields,
                });
              }
            )}
          {showRateLearningGoals && (
            <div
              className={evaluationContentClass}
              onClick={() => setShowRateLearningGoalsModal(true)}
              style={{
                marginLeft:
                  isPreviewCard && showAssessmentToolCard ? "16px" : "",
              }}
            >
              <div className={classes.evaluationIcon}>
                <GoalsOutlined size={"xx-small"} variant={"link"} />
              </div>
              <div className={classes.evaluationTitle}>
                {t("common:learning_goals")}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  );
};

const RateOnLearningGoalsModalContent = props => {
  const {
    isComplete,
    parentId,
    parentType,
    showToggleStudentPypEvaluateStatus,
    evaluateIBPYPElements,
    studentDetails,
    assessmentFields,
    ibPYPElementRatings,
    isEvaluated,
    curriculumProgramType,
    course,
    t,
    setShowRateLearningGoalsModal,
  } = props;
  return (
    <React.Fragment>
      <div className={classes.modalHeader}>
        <span>{t("common:learning_goals")}</span>
        <span className={classes.actionButtons}>
          <Button
            variant={"outlined"}
            size={"medium"}
            onClick={() => setShowRateLearningGoalsModal(false)}
          >
            {t("common:cancel")}
          </Button>
        </span>
      </div>
      <div className={classes.modalBody}>
        <div className={classes.innerWrapper}>
          <PypEvaluation
            isComplete={isComplete}
            parentId={parentId}
            parentType={parentType}
            showToggleStudentPypEvaluateStatus={
              showToggleStudentPypEvaluateStatus
            }
            evaluateIBPYPElements={evaluateIBPYPElements}
            studentDetails={studentDetails}
            assessmentFields={assessmentFields}
            ibPYPElementRatings={ibPYPElementRatings}
            isEvaluated={isEvaluated}
            curriculumProgramType={curriculumProgramType}
            course={course}
            isPreviewMode={true}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

const RateOnLearningGoalsPreviewModal = props => {
  const { setShowRateLearningGoalsModal } = props;
  return (
    <UIModal
      isOpen={true}
      modalContent={classes.modalContent}
      onRequestClose={() => setShowRateLearningGoalsModal(false)}
    >
      <RateOnLearningGoalsModalContent {...props} />
    </UIModal>
  );
};

const StudentResponse = props => {
  const {
    t,
    item,
    showSeeMore,
    containerStyle,
    attachmentListContainerStyle,
    responseStyle,
    horizontalCardStyle,
    showStudentProfile,
    showEvaluation,
    isPreviewCard,
    evaluationTextStyle,
    attachmentsContainerStyle,
    course,
    curriculumProgramType,
    getCriteriaSetOfPypType,
    defaultStudentIdentifierNamePreference,
  } = props;

  const [showAssessmentTool, setShowAssessmentTool] = useState(false);
  const [showRateLearningGoalsModal, setShowRateLearningGoalsModal] = useState(
    false
  );

  const [assessmentToolInfo, setAssessmentToolInfo] = useState({
    assessmentToolIdLocal: "",
    assessmentToolTypeLocal: "",
    isTeacherEvaluationEnabledLocal: false,
    isStudentEvaluationEnabledLocal: false,
  });

  const {
    assessmentToolIdLocal,
    assessmentToolTypeLocal,
    isTeacherEvaluationEnabledLocal,
    isStudentEvaluationEnabledLocal,
  } = assessmentToolInfo;

  const student = _.get(item, "student", {});
  const studentAssignmentId = _.get(item, "id", "");
  const content = _.get(item, "assignment.content", {});
  const isLearningGoalsEvaluationEnabled = _.get(
    content,
    "isLearningGoalsEvaluationEnabled",
    false
  );
  const assessmentToolEdges = _.get(content, "assessmentTools.edges", []);
  const assessmentTools = _.filter(assessmentToolEdges, ({ node }) => {
    const assessmentToolType = _.get(node, "assessmentToolType");
    return !_.includes(
      ["SCORE", "ASSESSMENT_GRADE_BOUNDARY"],
      assessmentToolType
    );
  });

  const submission = _.get(item, "submission", {});
  const response = _.get(submission, "response", "");
  const attachmentGroups = _.get(submission, "attachmentGroups", {});
  const attachmentsToDisplay = getAttachmentsToDisplayWithPostConfig({
    attachmentGroups,
  });
  const toolResponsesData = _.get(item, "toolResponses", {});
  const toolResponses = _.get(
    _.find(toolResponsesData, ({ toolId }) => toolId === assessmentToolIdLocal),
    "toolResponses",
    {}
  );

  const ibPYPElementRatings = _.get(item, "ibPYPElementRatings", []);

  const isTeacherEvaluationEnabled = _.get(
    item,
    "assignment.isTeacherEvaluationEnabled",
    true
  );

  const isStudentEvaluationEnabled = _.get(
    item,
    "assignment.isStudentEvaluationEnabled",
    true
  );

  const commentMessage = getStaffElementRemarkMessage(
    _.get(item, "elementRemarks", [])
  );

  const assessmentFields = _.get(
    content,
    "allFields",
    _.get(content, "fields", [])
  );
  const field_list = _.get(content, "template.body.field_list", []);
  const unitPlanFields = _.get(item, "unitPlanFields", []);

  const { assessmentToolText } = getAssessmentToolText({
    t,
    assessmentToolType: assessmentToolTypeLocal,
    assessmentTool: _.get(
      _.find(assessmentTools, ({ node }) => {
        const assessmentToolType = _.get(node, "assessmentToolType", "");
        return assessmentToolType === assessmentToolTypeLocal;
      }),
      "node.assessmentToolItem",
      {}
    ),
  });

  const isEmptyAchievementCriteria = checkAchievementCriteriaExists({
    assessmentFields,
    curriculumProgramType,
    getCriteriaSetOfPypType,
  });

  const showRateLearningGoals =
    isLearningGoalsEvaluationEnabled &&
    checkAssessmentHasPypEvaluation({
      assessmentDetails: content,
      curriculumProgramType,
    }) &&
    !isEmptyAchievementCriteria;

  const onClickResponse = e => {
    const { onClickResponse } = props;
    if (onClickResponse) {
      onClickResponse(e);
    }
  };

  const isAttachmentGroupEmpty = _.isEmpty(attachmentGroups);

  const innerContainerClass = classNames({
    [classes.innerContainer]: true,
    [classes.innerContainerPreview]: isPreviewCard,
  });

  const wrapperClass = classNames({
    [classes.wrapper]: !isPreviewCard,
    [classes.wrapperPreview]: isPreviewCard,
  });

  return (
    <div
      className={classes.container}
      style={containerStyle}
      onClick={onClickResponse}
    >
      <div className={innerContainerClass}>
        {showStudentProfile && (
          <StudentResponseProfile
            student={student}
            t={t}
            defaultStudentIdentifierNamePreference={
              defaultStudentIdentifierNamePreference
            }
          />
        )}
        {!_.isEmpty(response) && (
          <div className={classes.response} style={responseStyle}>
            {showSeeMore ? (
              <TextMoreLess value={response} minHeight={48} />
            ) : (
              response
            )}
          </div>
        )}

        <div className={wrapperClass}>
          {!isAttachmentGroupEmpty && (
            <div
              className={classes.attachmentListContainer}
              style={attachmentsContainerStyle}
            >
              <AttachmentList
                value={attachmentsToDisplay}
                mode={"view"}
                cardType={"horizontalV3"}
                showCreateDropdown={false}
                containerStyle={attachmentListContainerStyle}
                horizontalCardStyle={horizontalCardStyle}
              />
            </div>
          )}
          <StudentResponseEvaluation
            evaluationTextStyle={evaluationTextStyle}
            showEvaluation={showEvaluation}
            isPreviewCard={isPreviewCard}
            setShowAssessmentTool={setShowAssessmentTool}
            t={t}
            isAttachmentGroupEmpty={isAttachmentGroupEmpty}
            setShowRateLearningGoalsModal={setShowRateLearningGoalsModal}
            showRateLearningGoals={showRateLearningGoals}
            assessmentTools={assessmentTools}
            setAssessmentToolInfo={setAssessmentToolInfo}
            isTeacherEvaluationEnabled={isTeacherEvaluationEnabled}
            isStudentEvaluationEnabled={isStudentEvaluationEnabled}
            assessmentFields={assessmentFields}
          />
        </div>
        {showAssessmentTool && (
          <AssessmentToolPreviewModalV2
            id={assessmentToolIdLocal}
            type={assessmentToolTypeLocal}
            entityType={_.get(
              _.find(ASSESSMENT_TOOLS, { type: assessmentToolTypeLocal }),
              "entityType",
              ""
            )}
            mode={"view"}
            toolResponses={toolResponses}
            title={assessmentToolText}
            showChooseButton={false}
            onClickClose={() => setShowAssessmentTool(false)}
            isEvaluatedByStudent={isStudentEvaluationEnabledLocal}
            unitPlanFields={unitPlanFields}
            assessmentFields={assessmentFields}
            field_list={field_list}
            parentId={studentAssignmentId}
            parentType={"STUDENT_ASSIGNMENT"}
            studentid={_.get(student, "id", "")}
            ibPYPElementRatings={ibPYPElementRatings}
            isTeacherEvaluationEnabled={isTeacherEvaluationEnabledLocal}
            commentMessage={commentMessage}
            showEvaluateStatus={true}
          />
        )}
        {showRateLearningGoalsModal && (
          <RateOnLearningGoalsPreviewModal
            isComplete={true}
            parentId={studentAssignmentId}
            parentType={"STUDENT_ASSIGNMENT"}
            showToggleStudentPypEvaluateStatus={false}
            evaluateIBPYPElements={true}
            studentDetails={student}
            assessmentFields={assessmentFields}
            ibPYPElementRatings={ibPYPElementRatings}
            isEvaluated={true}
            curriculumProgramType={curriculumProgramType}
            course={course}
            setShowRateLearningGoalsModal={setShowRateLearningGoalsModal}
            t={t}
          />
        )}
      </div>
    </div>
  );
};

StudentResponse.defaultProps = {
  showStudentProfile: true,
  showEvaluation: true,
};

const mapStateToProps = (state, ownProps) => {
  const courseId = _.get(state, "teacher.selected_class.selected_course");
  const academicYearId = getSelectedAcademicYearId(state);
  const courseGradeFilters = {
    academicYearId,
  };
  const course = getCourseDetailsFromCache({
    id: courseId,
    courseGradeFilters,
  });
  const curriculumProgramType = _.get(
    state,
    "platform.currentCurriculumProgram.type",
    "IB_PYP"
  );
  const defaultStudentIdentifierNamePreference = getDefaultStudentIdentifierNamePreference(
    state
  );

  return {
    course,
    curriculumProgramType,
    defaultStudentIdentifierNamePreference,
  };
};

const mapActionCreators = {
  getCriteriaSetOfPypType,
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, mapActionCreators)
)(StudentResponse);
